<script lang="ts">
  import { gallery } from './lib/gallery.svelte';
  import Viewer from './components/Viewer.svelte';
  import MenuButton from './components/MenuButton.svelte';
  import FolderTree from './components/FolderTree.svelte';
  import ListingPanel from './components/ListingPanel.svelte';
  import NameOverlay from './components/NameOverlay.svelte';
  import ScrollHint from './components/ScrollHint.svelte';

  gallery.init();

  // Swipe the drawer left to close it.
  let touchX = 0;
  let touchY = 0;
  function drawerTouchStart(e: TouchEvent): void {
    touchX = e.touches[0]?.clientX ?? 0;
    touchY = e.touches[0]?.clientY ?? 0;
  }
  function drawerTouchEnd(e: TouchEvent): void {
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - touchX;
    const dy = t.clientY - touchY;
    // Predominantly-horizontal left swipe (won't trigger on vertical scrolling).
    if (dx < -50 && Math.abs(dx) > Math.abs(dy)) gallery.closePanel();
  }

  function onKeydown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'Escape':
        if (gallery.panelOpen) gallery.closePanel();
        break;
      case 'ArrowDown':
      case 'ArrowRight':
      case 'PageDown':
      case ' ':
        e.preventDefault();
        gallery.forward();
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
      case 'PageUp':
        e.preventDefault();
        gallery.step(-1);
        break;
    }
  }
</script>

<svelte:window
  onkeydown={onKeydown}
  oncontextmenu={(e) => e.preventDefault()}
  ondragstart={(e) => e.preventDefault()}
/>

{#if gallery.status === 'loading'}
  <div class="centered"><div class="spinner" aria-label="Loading"></div></div>
{:else if gallery.status === 'error'}
  <div class="centered">
    <p class="error">Couldn’t load the gallery.<br /><small>{gallery.errorMessage}</small></p>
  </div>
{:else}
  <Viewer />
  <NameOverlay />
  <ScrollHint />
  <MenuButton />

  <div
    class="backdrop"
    class:open={gallery.panelOpen}
    onclick={() => gallery.closePanel()}
    role="presentation"
  ></div>

  <aside
    class="drawer"
    class:open={gallery.panelOpen}
    aria-hidden={!gallery.panelOpen}
    ontouchstart={drawerTouchStart}
    ontouchend={drawerTouchEnd}
  >
    <nav class="folders" aria-label="Folders">
      {#if gallery.tree}
        <FolderTree node={gallery.tree} />
      {/if}
    </nav>
    <div class="listing-col">
      <ListingPanel />
    </div>
  </aside>
{/if}

<style>
  .centered {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }
  .error {
    color: var(--muted);
  }
  .spinner {
    width: 28px;
    height: 28px;
    border: 2px solid rgba(255, 255, 255, 0.25);
    border-top-color: var(--fg);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .backdrop {
    position: fixed;
    inset: 0;
    z-index: 10;
    /* Dim the exposed photo (no blur — only the drawer itself blurs the photo). */
    background: rgba(0, 0, 0, 0.32);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.35s var(--ease);
  }
  .backdrop.open {
    opacity: 1;
    pointer-events: auto;
  }

  .drawer {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 20;
    height: 100dvh;
    width: min(88vw, 560px);
    display: grid;
    /* Folders take a capped share; the photo listing always gets the larger
       remainder, so it never ends up squashed on narrow phone screens. */
    grid-template-columns: minmax(160px, 38%) 1fr;
    background: var(--panel-bg);
    /* Heavy blur + saturation lift = silky frosted glass over the photo. */
    backdrop-filter: blur(30px) saturate(180%);
    -webkit-backdrop-filter: blur(30px) saturate(180%);
    border-right: 1px solid var(--panel-border);
    /* Soft depth + a faint inner highlight along the leading edge. */
    box-shadow:
      0 0 60px rgba(0, 0, 0, 0.45),
      inset -1px 0 0 rgba(255, 255, 255, 0.04);
    transform: translateX(-100%);
    transition: transform 0.36s var(--ease);
    will-change: transform;
  }
  .drawer.open {
    transform: translateX(0);
  }

  .folders {
    overflow-y: auto;
    padding: 4rem 0.5rem 1rem;
    border-right: 1px solid var(--panel-divider);
    scrollbar-width: thin;
  }

  .listing-col {
    overflow: hidden;
    padding-top: 4rem;
  }
</style>
